import { NgModule } from '@angular/core';
import { CollectionManagerService, ZefEntitiesModule } from '@zerops/zef/entities';
import { ZefErrorsModule, ZefPopErrorModule, ZefSnackErrorModule } from '@zerops/zef/errors';
import { ZefProgressModule } from '@zerops/zef/progress';
import { ZefDialogModule } from '@zerops/zef/dialog';
import { ZefWebsocketModule } from '@zerops/zef/websocket';
import { ZefAuthModule } from '@zerops/zef/auth';
import { ZefFormsModule } from '@zerops/zef/forms';
import { ZefGithubModule } from '@zerops/zef/github';
import { ZefGitlabModule } from '@zerops/zef/gitlab';
import { ZefPipesModule } from '@zerops/zef/pipes';
import { ZefFadeAnimationModule } from '@zerops/zef/animations';
import { environment } from '@zerops/zerops/env';
import { ZefNgrxRouterModule } from '@zerops/zef/ngrx-router';
import { ZefStateSwitcherModule } from '@zerops/zef/state-switcher';
import { META_CONFIG, ZefMetaModule } from '@zerops/zef/meta';
import { HttpStatusCodes } from '@zerops/zef/core';
import { ApiEntityKeys } from '@zerops/zerops/app';
import { ClientUser } from '@zerops/models/client-user';
import { IntegrationToken, IntegrationTokenDelegation } from '@zerops/models/integration-token';
import { Project } from '@zerops/models/project';
import { AppVersion } from '@zerops/models/app-version';
import { HttpRouting } from '@zerops/models/http-routing';
import { PortRouting } from '@zerops/models/port-routing';
import { UserData } from '@zerops/models/user-data';

@NgModule({
  imports: [
    ZefEntitiesModule.forRoot({
      config: {
        apiPrefix: '/api'
      }
    }),
    ZefErrorsModule.forRoot({
      // we don't want to show 401 error, as it always
      // triggers refresh, which either successes and
      // "nothing" visible happens for the user or fails
      // in which case single 403 error is added and handled
      // by a dialog error component
      blacklistedErrors: [ HttpStatusCodes.UNAUTHORIZED ],
      uniqueErrors: [ HttpStatusCodes.FORBIDDEN ]
    }),
    ZefProgressModule.forRoot(),
    ZefAuthModule.forRoot({
      loginEndpoint: '/api/auth/login',
      logoutEndpoint: '/api/auth/logout',
      refreshEndpoint: '/api/auth/refresh',
      cookieDomain: environment.cookieDomain
    }),
    ZefGithubModule.forRoot({
      callbackUrl: environment.githubAuthCallbackUrl
    }),
    ZefGitlabModule.forRoot({
      callbackUrl: environment.gitlabAuthCallbackUrl
    }),
    ZefDialogModule.forRoot(),
    ZefFormsModule.forRoot(),
    ZefNgrxRouterModule.forRoot(),
    ZefStateSwitcherModule.forRoot(),
    ZefWebsocketModule.forRoot({
      loginUrl: environment.websocketLoginUrl,
      host: environment.websocketHost,
      forceSecuredEndpoint: environment.forceSecuredWebsocket
    }),
    ZefFadeAnimationModule,
    ZefPopErrorModule,
    ZefSnackErrorModule,
    ZefPipesModule,
    ZefMetaModule.forRoot(),
  ],
  providers: [
    {
      provide: META_CONFIG,
      useValue: {
        baseTitle: 'zerops.io',
        separator: '~'
      }
    },
  ]
})
export class ZefLibModule {
  constructor(private _collectionManagerService: CollectionManagerService) {
    this._collectionManagerService.create({
      [ApiEntityKeys.User]: {
        definitions: {
          clientUserList: [ ApiEntityKeys.ClientUser ]
        }
      },
      [ApiEntityKeys.ClientUser]: {
        definitions: {
          client: ApiEntityKeys.Client
        },
        urlPattern: {
          default: {
            pattern: 'client/:clientId/client-user',
            getParentIds: (entity: ClientUser, meta: any) => ({
              clientId: entity?.clientId || meta?.clientId
            })
          },
          operations: {
            search: 'flat'
          }
        }
      },
      [ApiEntityKeys.ServiceStack]: {},
      [ApiEntityKeys.ClientStatus]: {
        config: {
          idAttribute: 'clientId'
        }
      },
      [ApiEntityKeys.HttpRouting]: {
        urlPattern: {
          default: 'flat',
          operations: {
            add: {
              pattern: 'project/:projectId/public-http-routing',
              getParentIds: (entity: HttpRouting, meta: any) => ({
                projectId: entity?.projectId || meta?.projectId
              })
            }
          }
        }
      },
      [ApiEntityKeys.PortRouting]: {
        urlPattern: {
          default: 'flat',
          operations: {
            add: {
              pattern: 'service-stack/:serviceStackId/public-port-routing',
              getParentIds: (entity: PortRouting, meta: any) => ({
                serviceStackId: entity?.serviceStackId || meta?.serviceStackId
              })
            }
          }
        }
      },
      [ApiEntityKeys.Notification]: {},
      [ApiEntityKeys.Process]: {},
      [ApiEntityKeys.Project]: {
        urlPattern: {
          default: 'flat',
          operations: {
            add: {
              pattern: 'client/:clientId/project',
              getParentIds: (entity: Project, meta: any) => ({
                clientId: entity?.clientId || meta?.clientId
              })
            }
          }
        }
      },
      [ApiEntityKeys.Client]: {},
      [ApiEntityKeys.UserData]: {
        urlPattern: {
          default: 'flat',
          operations: {
            add: {
              pattern: 'service-stack/:serviceStackId/user-data',
              getParentIds: (entity: UserData, meta: any) => ({
                serviceStackId: entity?.serviceStackId || meta?.serviceStackId
              })
            }
          }
        }
      },
      [ApiEntityKeys.UserToken]: {},
      [ApiEntityKeys.IntegrationToken]: {
        urlPattern: {
          default: {
            pattern: 'client/:clientId/integration-token',
            getParentIds: (entity: IntegrationToken, meta: any) => ({
              clientId: entity?.clientId || meta?.clientId
            })
          }
        }
      },
      [ApiEntityKeys.IntegrationTokenDelegation]: {
        urlPattern: {
          // create (add) / getById / delete are nested under the parent token;
          // clientId + tokenId come from the entity when present, else from meta.
          default: {
            pattern: 'client/:clientId/integration-token/:tokenId/delegation',
            getParentIds: (entity: IntegrationTokenDelegation, meta: any) => ({
              clientId: entity?.clientId || meta?.clientId,
              tokenId: entity?.tokenId || meta?.tokenId
            })
          }
        }
      },
      [ApiEntityKeys.AppVersion]: {
        urlPattern: {
          default: 'flat',
          operations: {
            add: {
              pattern: 'service-stack/:serviceStackId/app-version',
              getParentIds: (entity: AppVersion, meta: any) => ({
                serviceStackId: entity?.serviceStackId || meta?.serviceStackId
              })
            }
          }
        }
      },
      [ApiEntityKeys.Container]: {},
      [ApiEntityKeys.Service]: {},
      [ApiEntityKeys.Payment]: {},
      [ApiEntityKeys.Subscription]: {},
      [ApiEntityKeys.TransactionDebit]: {},
      [ApiEntityKeys.TransactionCredit]: {},
      [ApiEntityKeys.ProjectStatus]: {},
      [ApiEntityKeys.StackEvent]: {},
      [ApiEntityKeys.FreeResourceSummary]: {}
    });
  }
}
